import { useCallback, useEffect, useMemo, useRef, useState } from "react"

import { MapView, type MapClickPoint } from "./components/MapView"
import { AutomatPopup } from "./components/AutomatPopup"
import { AddAutomatForm } from "./components/AddAutomatForm"
import { HereButton } from "./components/HereButton"
import { InstallHint } from "./components/InstallHint"
import { Onboarding } from "./components/Onboarding"
import { Toast } from "./components/Toast"
import * as api from "./lib/api"
import type { Bbox, ListAutomat } from "./types"

type Sheet =
  | { kind: "detail"; id: number }
  | { kind: "add"; pin: MapClickPoint | null }
  | null

export function App() {
  const [automaty, setAutomaty] = useState<ListAutomat[]>([])
  const [sheet, setSheet] = useState<Sheet>(null)
  const [toast, setToast] = useState<string | null>(null)
  const [flyTo, setFlyTo] = useState<{ lat: number; lon: number; zoom: number } | undefined>()
  const [bbox, setBbox] = useState<Bbox | null>(null)
  const lastBboxRef = useRef<string | null>(null)

  const showToast = useCallback((msg: string) => {
    setToast(msg)
    window.setTimeout(() => setToast((t) => (t === msg ? null : t)), 3200)
  }, [])

  // Load automaty on bbox changes (debounced in MapView).
  useEffect(() => {
    if (!bbox) return
    const key = bbox.join(",")
    if (lastBboxRef.current === key) return
    lastBboxRef.current = key
    api
      .listByBbox(bbox)
      .then((r) => setAutomaty(r.automaty))
      .catch((e: api.ApiError) => {
        if (e.code !== "bbox_too_large") showToast("Brak połączenia z serwerem")
      })
  }, [bbox, showToast])

  const reload = useCallback(() => {
    if (!bbox) return
    lastBboxRef.current = null
    api.listByBbox(bbox).then((r) => setAutomaty(r.automaty)).catch(() => {})
  }, [bbox])

  const onPinClick = useCallback((id: number) => {
    setSheet({ kind: "detail", id })
  }, [])

  const onMapClick = useCallback((p: MapClickPoint) => {
    setSheet((s) => (s?.kind === "add" ? { kind: "add", pin: p } : s))
  }, [])

  const isAddMode = sheet?.kind === "add"

  const openAdd = useCallback(() => {
    setSheet({ kind: "add", pin: null })
  }, [])

  const closeSheet = useCallback(() => setSheet(null), [])

  const onReport = useCallback(
    async (id: number, status: "ok" | "broken") => {
      try {
        const r = await api.reportStatus(id, status)
        showToast(r.current_status === "broken" ? "Zgłoszono jako niedziałający" : "Dzięki, zgłoszono")
        reload()
      } catch (e) {
        if (e instanceof api.ApiError && e.status === 429) {
          showToast("Spróbuj ponownie za chwilę (za dużo zgłoszeń)")
        } else {
          showToast("Nie udało się wysłać zgłoszenia")
        }
      }
    },
    [reload, showToast],
  )

  const onCreate = useCallback(
    async (body: { lat: number; lon: number; chain: string | null; name: string | null }) => {
      try {
        const r = await api.createAutomat(body)
        if (r.merged) {
          showToast("Ten automat już istnieje — otwieram szczegóły")
          setSheet({ kind: "detail", id: r.id })
        } else if (r.possible_duplicate_of) {
          showToast("Dodano (możliwy duplikat)")
          setSheet(null)
        } else {
          showToast("Dodano automat, dzięki!")
          setSheet(null)
        }
        reload()
      } catch (e) {
        if (e instanceof api.ApiError && e.status === 400) {
          showToast("Współrzędne poza Polską")
        } else if (e instanceof api.ApiError && e.status === 429) {
          showToast("Za dużo nowych zgłoszeń — spróbuj później")
        } else {
          showToast("Nie udało się dodać")
        }
      }
    },
    [reload, showToast],
  )

  const onGeolocate = useCallback(
    (lat: number, lon: number) => {
      setFlyTo({ lat, lon, zoom: 14 })
    },
    [],
  )

  const content = useMemo(() => {
    if (!sheet) return null
    if (sheet.kind === "detail") {
      return <AutomatPopup id={sheet.id} onReport={onReport} onClose={closeSheet} />
    }
    return <AddAutomatForm pin={sheet.pin} onSubmit={onCreate} onClose={closeSheet} />
  }, [sheet, onReport, onCreate, closeSheet])

  return (
    <div className="app">
      <header className="topbar">
        <span className="title">automaty.de</span>
        <button onClick={() => (window.location.href = "/polityka-prywatnosci.html")}>
          Prywatność
        </button>
      </header>
      <div className="map-wrap">
        <MapView
          automaty={automaty}
          onBboxChange={setBbox}
          onPinClick={onPinClick}
          onMapClick={onMapClick}
          pickMode={isAddMode}
          pendingPin={sheet?.kind === "add" ? sheet.pin : null}
          flyTo={flyTo}
        />
        {!sheet && (
          <>
            <button className="fab add" onClick={openAdd} aria-label="Dodaj automat">
              + Dodaj automat
            </button>
            <HereButton onLocate={onGeolocate} onError={showToast} />
          </>
        )}
        {content}
        <footer className="footer">
          © automaty.de · dane map: © OpenMapTiles / OpenStreetMap
        </footer>
      </div>
      <InstallHint />
      <Onboarding />
      {toast && <Toast>{toast}</Toast>}
    </div>
  )
}
